import { useApolloClient } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { ReactionType } from '../../graphqlEnums'

export class CommentDeletedError extends Error {
  constructor() {
    super('The comment you are trying to react to could have been deleted')
    this.name = 'CommentDeletedError'
  }
}

/**
 * Fragment to update LastReply cache entries.
 * LastReply has a different __typename than BaseComment, so Apollo doesn't
 * auto-merge the mutation result. We manually update the LastReply cache.
 */
const LastReplyReactionsFragment = graphql(/* GraphQL */ `
  fragment LastReplyReactions on LastReply {
    reactions {
      type
    }
  }
`)

/**
 * Fragment to mark a CommentFromThirdParty as deleted in the cache.
 * Used when the API returns a NotFoundError, indicating the comment was deleted.
 */
const CommentFromThirdPartyDeletedFragment = graphql(/* GraphQL */ `
  fragment CommentFromThirdPartyDeleted on CommentFromThirdParty {
    isDeleted
  }
`)

/**
 * Fragment to mark a CommentFromBuffer as deleted in the cache.
 * Used when the API returns a NotFoundError, indicating the comment was deleted.
 */
const CommentFromBufferDeletedFragment = graphql(/* GraphQL */ `
  fragment CommentFromBufferDeleted on CommentFromBuffer {
    isDeleted
  }
`)

export const ReactToCommentMutationGQL = graphql(/* GraphQL */ `
  mutation ReactToComment($input: ReactToCommentInput!) {
    reactToComment(input: $input) {
      __typename
      ... on ReactToCommentSuccess {
        comment {
          ... on BaseComment {
            __typename
            id
            status
            text
            updatedAt
            parentId
            reactions {
              type
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on MutationError {
        message
      }
    }
  }
`)

type UseReactToCommentResult = {
  reactToComment: (
    commentId: string,
    isActiveReaction: boolean,
    reactionType?: ReactionType,
  ) => Promise<void>
  loading: boolean
}

/**
 * Hook for executing comment reaction GraphQL mutations.
 *
 * This hook provides an interface for liking/unliking comments:
 * - Executes the ReactToComment GraphQL mutation
 * - Updates the comment's reactions in the Apollo cache
 * - Returns a boolean indicating success/failure
 *
 * @returns Object containing reactToComment function and loading state
 *
 * @example
 * ```typescript
 * const { reactToComment, loading } = useReactToComment()
 *
 * const handleLike = async (commentId: string) => {
 *   await reactToComment(commentId, true)
 
 * }
 * ```
 */
export const useReactToComment = (): UseReactToCommentResult => {
  const client = useApolloClient()
  const [mutate, { loading }] = useTypedMutation(
    ReactToCommentMutationGQL,
    (data) => data.reactToComment,
    {
      successTypename: 'ReactToCommentSuccess',
    },
  )

  const reactToComment = async (
    commentId: string,
    isActiveReaction: boolean,
    reactionType?: ReactionType,
  ): Promise<void> => {
    let result
    try {
      result = await mutate({
        variables: {
          input: {
            commentId,
            isActiveReaction,
            ...(reactionType && { reactionType }),
          },
        },
      })
    } catch (error) {
      // Network or unexpected error during mutation
      // eslint-disable-next-line no-console
      console.error('Unexpected error while reacting to comment:', error)
      toast.error(
        'An unexpected error occurred while reacting to the comment. Please try again.',
        { duration: 2_000 },
      )
      throw error
    }

    // Also update LastReply cache entry if it exists.
    // LastReply has a different __typename than BaseComment, so Apollo
    // doesn't auto-merge the mutation result for lastReply comments.
    if (result.success && result.data?.comment) {
      const { reactions } = result.data.comment
      try {
        client.writeFragment({
          id: `LastReply:${commentId}`,
          fragment: LastReplyReactionsFragment,
          data: { reactions },
        })
      } catch {
        // LastReply cache entry doesn't exist, which is fine
      }
    }

    // Handle API errors (not network errors)
    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error('Error while reacting to comment:', result.error)

      const isNotFoundError = result.error?.__typename === 'NotFoundError'

      // If comment was not found, mark it as deleted in the cache.
      // This allows the UI to show CommentDeletedError immediately
      // without waiting for a page refresh.
      // We try both concrete types since we don't know which one the comment is.
      if (isNotFoundError) {
        client.writeFragment({
          id: `CommentFromThirdParty:${commentId}`,
          fragment: CommentFromThirdPartyDeletedFragment,
          data: { isDeleted: true },
        })

        client.writeFragment({
          id: `CommentFromBuffer:${commentId}`,
          fragment: CommentFromBufferDeletedFragment,
          data: { isDeleted: true },
        })
      }

      if (isNotFoundError) {
        const error = new CommentDeletedError()
        toast.error(error.message, { duration: 2_000 })
        throw error
      }

      const errorMessage =
        'An error occurred while reacting to the comment. Please try again.'
      toast.error(errorMessage, { duration: 2_000 })
      throw new Error(errorMessage)
    }
  }

  return {
    reactToComment,
    loading,
  }
}
